/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowUpRight, Bell, Bot, Eye, EyeOff, Key, Link as LinkIcon, RefreshCw, Save, SlidersHorizontal, Sparkles, User, Users,
} from 'lucide-react';
import { fetchSettings, updateSettings } from '../store/slices/authSlice';
import api from '../services/api';
import ExpertsSettings from '../components/ExpertsSettings';
import NotificationsSettings from '../components/NotificationsSettings';
import CalendarSubscription from '../components/CalendarSubscription';
import { MASCOTS } from '../components/mascotCatalog';
import { useBroker } from '../components/shell/Broker';
import { useToast } from '../contexts/ToastContext';
import { cx } from '../theme/cx';
import { formatDate } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, Field, IconButton, Input, PageHeader, RadioCards, RadioMark, Select, Spinner, Tabs, UserAvatar, useRadioGroup,
} from '../components/ui';

const TABS = [
  { id: 'general', label: 'Général', icon: SlidersHorizontal },
  { id: 'mascot', label: 'Mascotte', icon: Sparkles },
  { id: 'experts', label: 'Experts', icon: Users },
  { id: 'notifications', label: 'Notifications', icon: Bell },
].map((tab) => ({ ...tab, tabId: `settings-tab-${tab.id}`, panelId: `settings-panel-${tab.id}` }));

const AI_PROVIDERS = [
  { value: 'openai', label: 'OpenAI', keyHint: 'platform.openai.com' },
  { value: 'gemini', label: 'Google Gemini', keyHint: 'ai.google.dev' },
];

const formFrom = (settings) => ({
  ai_provider: settings?.ai_provider || 'openai',
  ai_model: settings?.ai_model || 'gpt-4',
  ai_api_key: '',
  mascot_variant: settings?.mascot_variant || 'gladys',
});

const modelLabel = (m) => (m.displayName && m.displayName !== m.id ? `${m.displayName} (${m.id})` : m.id);

const Fact = ({ label, children }) => (
  <div className="bg-surface-1 px-4 py-3.5">
    <dt className="eyebrow">{label}</dt>
    <dd className="mt-1.5 text-sm font-medium text-fg">{children}</dd>
  </div>
);

const ConnectionCard = () => {
  const { name, status, authUrl } = useBroker();
  const badge = {
    checking: <Badge tone="neutral" dot="pulse">Vérification…</Badge>,
    offline: <Badge tone="loss" dot>Serveur injoignable</Badge>,
    connected: <Badge tone="gain" dot>Connecté</Badge>,
    disconnected: <Badge tone="warn" dot>Non connecté</Badge>,
  }[status];
  return (
    <Card>
      <CardHeader icon={LinkIcon} title={`Connexion ${name}`} description="Accès à votre compte pour importer le portefeuille." actions={badge} />
      {status === 'connected' && <Banner tone="success">Votre compte {name} est connecté et synchronisé.</Banner>}
      {status === 'offline' && <Banner tone="loss">Le serveur ne répond pas : l’état de la connexion sera vérifié dès son retour.</Banner>}
      {status === 'checking' && <Spinner label="Vérification de la connexion…" />}
      {status === 'disconnected' && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-sm leading-relaxed text-fg-muted">
            Connectez votre compte {name} pour importer votre portefeuille et suivre sa valorisation en temps réel.
          </p>
          {authUrl && <Button as="a" href={authUrl} trailingIcon={ArrowUpRight} className="self-start sm:self-auto">Connecter {name}</Button>}
        </div>
      )}
    </Card>
  );
};

const MascotGrid = ({ value, onChange }) => {
  const itemProps = useRadioGroup({ options: MASCOTS, value, onChange, getValue: (m) => m.id });
  return (
    <div role="radiogroup" aria-label="Mascotte" className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
      {MASCOTS.map((mascot, index) => {
        const checked = mascot.id === value;
        return (
          <button
            key={mascot.id}
            {...itemProps(mascot, index)}
            className={cx(
              'group relative overflow-hidden rounded-card border text-left transition-[border-color,box-shadow,background-color] duration-150',
              checked ? 'border-gold-500/70 bg-gold-950/40 shadow-[0_0_0_1px_rgb(var(--c-gold-500)/0.55)]' : 'border-line bg-surface-2/40 hover:border-line-strong hover:bg-surface-2',
            )}
          >
            <img src={mascot.img} alt="" className="h-44 w-full bg-canvas object-cover object-top" />
            <RadioMark checked={checked} className={cx('absolute right-3 top-3 shadow-pop', !checked && 'bg-canvas/70 opacity-0 group-hover:opacity-100')} />
            <span className="block p-4">
              <span className={cx('block font-medium', checked ? 'text-fg' : 'text-fg-body')}>{mascot.name}</span>
              <span className="mt-1 block text-sm leading-relaxed text-fg-muted">{mascot.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

const SaveBar = ({ saving, error, label = 'Enregistrer', onClick }) => (
  <div className="mt-6 space-y-4 border-t border-line pt-5">
    {error && <Banner tone="loss" role="alert">{error}</Banner>}
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
      <Button type={onClick ? 'button' : 'submit'} onClick={onClick} loading={saving} leadingIcon={Save}>{label}</Button>
    </div>
  </div>
);

const Settings = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { user, settings } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'general';

  const [formData, setFormData] = useState(() => formFrom(settings));
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const modelCache = useRef({});
  const hasApiKey = Boolean(settings?.has_api_key);
  const set = (patch) => setFormData((f) => ({ ...f, ...patch }));

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) setFormData(formFrom(settings));
  }, [settings]);

  const loadModels = useCallback(async (provider, force = false) => {
    if (!force && modelCache.current[provider]) {
      setModels(modelCache.current[provider]);
      return;
    }
    setModelsLoading(true);
    try {
      const res = await api.get(`/ai-models?provider=${provider}`);
      const list = res.data.models || [];
      modelCache.current[provider] = list;
      setModels(list);
    } catch {
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasApiKey && formData.ai_provider) loadModels(formData.ai_provider);
  }, [hasApiKey, formData.ai_provider, loadModels]);

  const selectTab = (id) => {
    setError('');
    setSearchParams(id === 'general' ? {} : { tab: id }, { replace: true });
  };

  const save = async (event, successTitle) => {
    event?.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.ai_api_key) delete payload.ai_api_key;
      await dispatch(updateSettings(payload)).unwrap();
      toast.success(successTitle);
      // Reload so the stored key comes back masked and has_api_key is fresh.
      dispatch(fetchSettings());
    } catch {
      setError('Impossible d’enregistrer les réglages. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const provider = AI_PROVIDERS.find((p) => p.value === formData.ai_provider) || AI_PROVIDERS[0];
  const savedKey = settings?.ai_api_key && settings.ai_api_key !== '********' ? settings.ai_api_key : '';

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <PageHeader eyebrow="Compte" title="Réglages" description="Votre compte, la connexion au courtier, l’intelligence artificielle, l’assistante Gladys et les notifications." />

      <Tabs items={TABS} value={activeTab} onChange={selectTab} ariaLabel="Sections des réglages" />

      {activeTab === 'general' && (
        <form id={TABS[0].panelId} role="tabpanel" aria-labelledby={TABS[0].tabId} onSubmit={(e) => save(e, 'Réglages enregistrés')} className="space-y-6">
          <Card>
            <CardHeader icon={User} title="Compte" />
            <div className="flex items-center gap-4">
              <UserAvatar email={user?.email} size="lg" />
              <p className="min-w-0 truncate font-medium text-fg">{user?.email || '—'}</p>
            </div>
            <dl className="mt-5 grid gap-px overflow-hidden rounded-control border border-line bg-line sm:grid-cols-2">
              <Fact label="Membre depuis">{formatDate(user?.created_at, 'long')}</Fact>
              <Fact label="Type de compte">Investisseur particulier</Fact>
            </dl>
          </Card>

          <ConnectionCard />

          <Card>
            <CardHeader icon={Bot} title="Intelligence artificielle" description="Fournisseur et modèle utilisés pour les analyses, les actualités et Gladys." />
            <div className="space-y-6">
              <RadioCards
                ariaLabel="Fournisseur d’IA"
                options={AI_PROVIDERS.map((p) => ({ ...p, description: `Clé disponible sur ${p.keyHint}` }))}
                value={formData.ai_provider}
                onChange={(v) => { if (v !== formData.ai_provider) set({ ai_provider: v, ai_model: '' }); }}
                className="sm:grid-cols-2"
              />

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label htmlFor="settings-model" className="text-sm font-medium text-fg-body">Modèle</label>
                  {hasApiKey && (
                    <Button variant="ghost" size="sm" leadingIcon={RefreshCw} loading={modelsLoading} onClick={() => loadModels(formData.ai_provider, true)}>
                      Actualiser
                    </Button>
                  )}
                </div>
                <Select id="settings-model" value={formData.ai_model} onChange={(e) => set({ ai_model: e.target.value })} disabled={modelsLoading} aria-busy={modelsLoading || undefined}>
                  {!formData.ai_model && <option value="" disabled>Choisir un modèle…</option>}
                  {formData.ai_model && !models.some((m) => m.id === formData.ai_model) && <option value={formData.ai_model}>{formData.ai_model}</option>}
                  {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
                </Select>
                <p className={cx('mt-1.5 text-xs leading-relaxed', hasApiKey ? 'text-fg-subtle' : 'text-warn-300')}>
                  {!hasApiKey && 'Enregistrez d’abord une clé API pour charger les modèles disponibles.'}
                  {hasApiKey && (modelsLoading ? 'Chargement des modèles…' : models.length > 0 ? `${models.length} modèles disponibles chez ${provider.label}.` : 'Aucun modèle chargé : utilisez « Actualiser ».')}
                </p>
              </div>

              <Field
                label="Clé API"
                htmlFor="settings-api-key"
                hint={`${savedKey ? `Clé enregistrée : ${savedKey}. Laissez le champ vide pour la conserver. ` : ''}Obtenez une clé sur ${provider.keyHint}.`}
              >
                <Input
                  id="settings-api-key"
                  type={showApiKey ? 'text' : 'password'}
                  autoComplete="off"
                  spellCheck={false}
                  leadingIcon={Key}
                  value={formData.ai_api_key}
                  onChange={(e) => set({ ai_api_key: e.target.value })}
                  placeholder={settings?.ai_api_key ? '••••••••••••' : 'Saisissez votre clé API'}
                  trailing={(
                    <IconButton
                      icon={showApiKey ? EyeOff : Eye}
                      label={showApiKey ? 'Masquer la clé' : 'Afficher la clé'}
                      pressed={showApiKey}
                      size="sm"
                      tooltipSide="left"
                      onClick={() => setShowApiKey((v) => !v)}
                    />
                  )}
                />
              </Field>
            </div>
            <SaveBar saving={saving} error={error} />
          </Card>
        </form>
      )}

      {activeTab === 'mascot' && (
        <div id={TABS[1].panelId} role="tabpanel" aria-labelledby={TABS[1].tabId}>
          <Card>
            <CardHeader icon={Sparkles} title="Apparence de Gladys" description="Choisissez la mascotte qui accompagne l’assistante dans le chat." />
            <MascotGrid value={formData.mascot_variant} onChange={(id) => set({ mascot_variant: id })} />
            <SaveBar saving={saving} error={error} onClick={(e) => save(e, 'Mascotte enregistrée')} />
          </Card>
        </div>
      )}

      {activeTab === 'experts' && (
        <div id={TABS[2].panelId} role="tabpanel" aria-labelledby={TABS[2].tabId}>
          <ExpertsSettings />
        </div>
      )}

      {activeTab === 'notifications' && (
        <div id={TABS[3].panelId} role="tabpanel" aria-labelledby={TABS[3].tabId} className="space-y-6">
          <NotificationsSettings />
          <CalendarSubscription />
        </div>
      )}
    </div>
  );
};

export default Settings;
